/**
 * Portal Admin Service - Manages portal settings, parent accounts, and storybooks
 * 
 * Uses explicit type guards to ensure type safety between DB and app types.
 */

import { supabase } from '../lib/supabase';
import { str, num, bool } from '../lib/guards';
import { logger } from '../lib/logger';

// =============================================================================
// TYPES (match DB schema, nullable where DB is nullable)
// =============================================================================

export interface PortalSettings {
  id: string;
  founder_image_url: string | null;
  founder_name: string | null;
  default_intro_message: string | null;
  default_voice_id: string | null;
  updated_at: string | null;
}

export interface ParentAccount {
  id: string;
  user_id: string | null;
  name: string;
  email: string | null;
  is_parent: boolean | null;
  password_change_required: boolean | null;
  created_at: string | null;
}

export interface PublishedStorybookWithAccess {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  cover_image_url: string | null;
  is_public: boolean | null;
  is_published: boolean | null;
  age_range_min: number | null;
  age_range_max: number | null;
  created_at: string | null;
  page_count: number;
  access_count: number;
}

export interface RecordingWithDetails {
  id: string;
  storybook_id: string;
  parent_id: string;
  child_name: string | null;
  audio_url: string;
  duration_seconds: number;
  recorded_at: string | null;
  notes: string | null;
  is_favorite: boolean | null;
  created_at: string | null;
  parent_name: string;
  parent_email: string | null;
  storybook_title: string;
  storybook_cover: string | null;
}

export interface StorybookIntro {
  id: string;
  storybook_id: string;
  parent_id: string;
  intro_audio_url: string;
  created_at: string | null;
  updated_at: string | null;
  parent_name: string;
  parent_email: string | null;
}

// =============================================================================
// PORTAL SETTINGS
// =============================================================================

export async function getPortalSettings(): Promise<PortalSettings | null> {
  try {
    const { data, error } = await supabase
      .from('portal_settings')
      .select('*')
      .eq('id', 'global')
      .maybeSingle();

    if (error) {
      logger.error('Error fetching portal settings:', error);
      return null;
    }

    if (!data) {
      return null;
    }

    return {
      id: data.id,
      founder_image_url: data.founder_image_url,
      founder_name: data.founder_name,
      default_intro_message: data.default_intro_message,
      default_voice_id: data.default_voice_id,
      updated_at: data.updated_at,
    };
  } catch (error) {
    logger.error('Failed to fetch portal settings:', error);
    return null;
  }
}

export async function updatePortalSettings(
  settings: Partial<Omit<PortalSettings, 'id' | 'updated_at'>>
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('portal_settings')
      .update(settings)
      .eq('id', 'global');

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function uploadFounderImage(file: File): Promise<string | null> {
  try {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop();
    const path = `founder/founder-image-${timestamp}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('storybook-assets')
      .upload(path, file, { cacheControl: '3600', upsert: true });

    if (uploadError) {
      logger.error('Upload error:', uploadError);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from('storybook-assets')
      .getPublicUrl(path);

    return urlData.publicUrl;
  } catch (error) {
    logger.error('Failed to upload founder image:', error);
    return null;
  }
}

// =============================================================================
// PARENT ACCOUNTS
// =============================================================================

export async function getParentAccounts(): Promise<ParentAccount[]> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('is_parent', true)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error fetching parent accounts:', error);
      return [];
    }

    return (data || []).map(p => ({
      id: p.id,
      user_id: p.user_id,
      name: p.name,
      email: p.email,
      is_parent: p.is_parent,
      password_change_required: p.password_change_required,
      created_at: p.created_at,
    }));
  } catch (error) {
    logger.error('Failed to fetch parent accounts:', error);
    return [];
  }
}

export async function createParentAccount(
  email: string,
  password: string,
  name: string
): Promise<{ success: boolean; user?: unknown; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('create-parent-account', {
      body: { email, password, name }
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data.success) {
      return { success: false, error: data.error };
    }

    return { success: true, user: data.user };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function deleteParentAccount(
  profileId: string,
  reason?: string
): Promise<{ success: boolean; error?: string; childrenDeleted?: number }> {
  try {
    // Use edge function with service role to properly delete both profile AND auth user
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-parent-account`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ profileId, reason }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to delete account' };
    }

    if (data.warning) {
      // Partial success - profile deleted but auth user remains
      logger.warn('Partial deletion:', data.warning);
    }

    return { 
      success: true, 
      childrenDeleted: data.childrenDeleted 
    };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

// =============================================================================
// PUBLISHED STORYBOOKS
// =============================================================================

export async function getPublishedStorybooks(): Promise<PublishedStorybookWithAccess[]> {
  try {
    const { data, error } = await supabase
      .from('published_storybooks')
      .select(`
        *,
        storybook_pages(count),
        storybook_access(count)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error fetching storybooks:', error);
      return [];
    }

    return (data || []).map(book => ({
      id: book.id,
      title: book.title,
      slug: book.slug,
      description: book.description,
      cover_image_url: book.cover_image_url,
      is_public: book.is_public,
      is_published: book.is_published,
      age_range_min: book.age_range_min,
      age_range_max: book.age_range_max,
      created_at: book.created_at,
      page_count: num(((book.storybook_pages as unknown[])?.[0] as Record<string, unknown>)?.count),
      access_count: num(((book.storybook_access as unknown[])?.[0] as Record<string, unknown>)?.count),
    }));
  } catch (error) {
    logger.error('Failed to fetch storybooks:', error);
    return [];
  }
}

export async function updateStorybookStatus(
  storybookId: string,
  isPublished: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('published_storybooks')
      .update({ is_published: isPublished })
      .eq('id', storybookId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function updateStorybookVisibility(
  storybookId: string,
  isPublic: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('published_storybooks')
      .update({ is_public: isPublic })
      .eq('id', storybookId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function getStorybookAccess(
  storybookId: string
): Promise<{ parentId: string; parentName: string; grantedAt: string }[]> {
  try {
    const { data, error } = await supabase
      .from('storybook_access')
      .select(`
        parent_id,
        granted_at,
        profiles!storybook_access_parent_id_fkey(name)
      `)
      .eq('storybook_id', storybookId);

    if (error) {
      logger.error('Error fetching access:', error);
      return [];
    }

    return (data || []).map(item => ({
      parentId: str(item.parent_id),
      parentName: str((item.profiles as Record<string, unknown>)?.name, 'Unknown'),
      grantedAt: str(item.granted_at),
    }));
  } catch (error) {
    logger.error('Failed to fetch storybook access:', error);
    return [];
  }
}

export async function grantStorybookAccess(
  storybookId: string,
  parentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('storybook_access')
      .insert({ storybook_id: storybookId, parent_id: parentId });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function revokeStorybookAccess(
  storybookId: string,
  parentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('storybook_access')
      .delete()
      .eq('storybook_id', storybookId)
      .eq('parent_id', parentId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function deleteStorybook(
  storybookId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('published_storybooks')
      .delete()
      .eq('id', storybookId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

// =============================================================================
// READING RECORDINGS
// =============================================================================

export async function getAllRecordings(): Promise<RecordingWithDetails[]> {
  try {
    const { data, error } = await supabase
      .from('reading_recordings')
      .select(`
        *,
        profiles!reading_recordings_parent_id_fkey(name, email),
        published_storybooks!reading_recordings_storybook_id_fkey(title, cover_image_url)
      `)
      .order('recorded_at', { ascending: false });

    if (error) {
      logger.error('Error fetching recordings:', error);
      return [];
    }

    return (data || []).map(r => ({
      id: r.id,
      storybook_id: r.storybook_id,
      parent_id: r.parent_id,
      child_name: r.child_name,
      audio_url: r.audio_url,
      duration_seconds: r.duration_seconds,
      recorded_at: r.recorded_at,
      notes: r.notes,
      is_favorite: r.is_favorite,
      created_at: r.created_at,
      parent_name: str((r.profiles as Record<string, unknown>)?.name, 'Unknown'),
      parent_email: (r.profiles as Record<string, unknown>)?.email as string | null,
      storybook_title: str((r.published_storybooks as Record<string, unknown>)?.title, 'Unknown'),
      storybook_cover: (r.published_storybooks as Record<string, unknown>)?.cover_image_url as string | null,
    }));
  } catch (error) {
    logger.error('Failed to fetch recordings:', error);
    return [];
  }
}

export async function deleteRecording(
  recordingId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('reading_recordings')
      .delete()
      .eq('id', recordingId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

// =============================================================================
// PERSONALIZED STORYBOOK INTROS
// =============================================================================

export async function getStorybookIntros(storybookId: string): Promise<StorybookIntro[]> {
  try {
    const { data, error } = await supabase
      .from('storybook_intros')
      .select(`
        *,
        profiles!storybook_intros_parent_id_fkey(name, email)
      `)
      .eq('storybook_id', storybookId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error fetching storybook intros:', error);
      return [];
    }

    return (data || []).map(intro => ({
      id: intro.id,
      storybook_id: intro.storybook_id,
      parent_id: intro.parent_id,
      intro_audio_url: intro.intro_audio_url,
      created_at: intro.created_at,
      updated_at: intro.updated_at,
      parent_name: str((intro.profiles as Record<string, unknown>)?.name, 'Unknown'),
      parent_email: (intro.profiles as Record<string, unknown>)?.email as string | null,
    }));
  } catch (error) {
    logger.error('Failed to fetch storybook intros:', error);
    return [];
  }
}

export async function createStorybookIntro(
  storybookId: string,
  parentId: string,
  audioBlob: Blob
): Promise<{ success: boolean; intro?: StorybookIntro; error?: string }> {
  try {
    const timestamp = Date.now();
    const ext = audioBlob.type.split('/')[1] || 'wav';
    const path = `intros/${storybookId}/${parentId}-${timestamp}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('storybook-assets')
      .upload(path, audioBlob, {
        cacheControl: '3600',
        upsert: true,
        contentType: audioBlob.type || 'audio/wav'
      });

    if (uploadError) {
      logger.error('Upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    const { data: urlData } = supabase.storage
      .from('storybook-assets')
      .getPublicUrl(path);

    const introAudioUrl = urlData.publicUrl;

    const { data, error } = await supabase
      .from('storybook_intros')
      .upsert({
        storybook_id: storybookId,
        parent_id: parentId,
        intro_audio_url: introAudioUrl,
        updated_at: new Date().toISOString()
      }, { onConflict: 'storybook_id,parent_id' })
      .select()
      .single();

    if (error) {
      logger.error('Error creating intro:', error);
      return { success: false, error: error.message };
    }

    return { 
      success: true, 
      intro: {
        id: data.id,
        storybook_id: data.storybook_id,
        parent_id: data.parent_id,
        intro_audio_url: data.intro_audio_url,
        created_at: data.created_at,
        updated_at: data.updated_at,
        parent_name: '', // Not fetched on create
        parent_email: null,
      }
    };
  } catch (error) {
    logger.error('Failed to create storybook intro:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function deleteStorybookIntro(
  introId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('storybook_intros')
      .delete()
      .eq('id', introId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

export async function getIntroForParent(
  storybookId: string,
  parentId: string
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('storybook_intros')
      .select('intro_audio_url')
      .eq('storybook_id', storybookId)
      .eq('parent_id', parentId)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching intro for parent:', error);
      return null;
    }

    return data?.intro_audio_url ?? null;
  } catch (error) {
    logger.error('Failed to fetch intro for parent:', error);
    return null;
  }
}
